<script setup lang="ts">
import {computed, PropType, useSlots} from 'vue';

// props, context, emits, libs
defineProps({
    disabled: {
        type: Boolean,
        required: false,
        default: false,
    },
    variant: {
        type: String as PropType<'contained' | 'outlined'>,
        required: false,
        default: 'contained',
    },
    color: {
        type: String as PropType<
            'primary' | 'secondary' | 'success' | 'danger'
        >,
        required: false,
        default: 'primary',
    },
});

const slots = useSlots();
const hasIconSlot = computed(() => !!slots.icon);
</script>

<template>
    <button
        v-bind="$attrs"
        :disabled="disabled"
        :class="{
            'base-button': true,
            contained: variant === 'contained',
            outlined: variant === 'outlined',
            primary: color === 'primary',
            secondary: color === 'secondary',
            success: color === 'success',
            danger: color === 'danger',
            disabled,
        }"
    >
        <slot></slot>
        <div v-if="hasIconSlot" class="icon">
            <slot name="icon"></slot>
        </div>
    </button>
</template>

<style lang="scss" scoped>
.base-button {
    display: flex;
    align-items: center;
    justify-content: center;
    outline: none;
    min-width: fit-content;
    font-weight: bold;
    height: 38px;
    padding: 0 16px;
    border-radius: 6px;
    opacity: 1;
    transition: opacity 0.35s ease;
    white-space: nowrap;

    &:hover:not([disabled]) {
        opacity: 0.7;
    }

    .icon {
        margin-left: 16px;
        padding-left: 16px;
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        border-left: 1px solid rgba(255, 255, 255, 0.2);
    }
}

.disabled {
    border: 1px solid $black_08 !important;
    background-color: $black_08 !important;
    color: $black_16 !important;
    cursor: not-allowed;

    &.outlined {
        border: 1px solid $black_16 !important;
        background-color: $white !important;
        color: $black_16 !important;
        cursor: not-allowed;
    }

    .icon {
        border-left: 1px solid $black_08;

        &:deep(img) {
            filter: opacity(0.5) drop-shadow(0 0 0 $black_80) !important;
        }
    }
}

.contained {
    &.primary {
        border: 1px solid $primary;
        background-color: $primary;
        color: $white;
    }

    &.secondary {
        border: 1px solid $secondary;
        background-color: $secondary;
        color: $white;
    }

    &.success {
        border: 1px solid $success;
        background-color: $success;
        color: $white;
    }

    &.danger {
        border: 1px solid $danger !important;
        background-color: $danger !important;
        color: $white;
    }
}

.outlined {
    &.primary {
        border: 1px solid $primary;
        background-color: $white;
        color: $primary;
    }
    &.secondary {
        border: 1px solid $secondary;
        background-color: $white;
        color: $secondary;
    }

    &.success {
        border: 1px solid $success;
        background-color: $white;
        color: $success;
    }

    &.danger {
        border: 1px solid $danger;
        background-color: $white;
        color: $danger;
    }
}
</style>
